import { useState, useEffect } from 'react';
import { X, CreditCard, Smartphone, Wallet, Check } from 'lucide-react';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderTotal: number;
  onPaymentComplete: (paymentData: {
    method: 'CASH' | 'MPESA' | 'CARD';
    amount: number;
    discount: number;
  }) => void;
}

export function PaymentModal({ isOpen, onClose, orderTotal, onPaymentComplete }: PaymentModalProps) {
  const [selectedDiscount, setSelectedDiscount] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState<'CASH' | 'MPESA' | 'CARD'>('CASH');
  const [cashAmount, setCashAmount] = useState('');
  
  const discountOptions = [0, 5, 10, 15, 20];
  
  // Calculate final amount after discount
  const discountAmount = orderTotal * (selectedDiscount / 100);
  const finalAmount = orderTotal - discountAmount;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDiscount(0);
      setSelectedMethod('CASH');
      setCashAmount('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCompletePayment = () => {
    if (selectedMethod === 'CASH' && !cashAmount) {
      return; // Don't allow payment if no cash amount entered
    }

    onPaymentComplete({
      method: selectedMethod,
      amount: finalAmount,
      discount: selectedDiscount,
    });
    onClose();
  };

  const isPaymentReady = () => {
    if (selectedMethod === 'CASH') {
      return cashAmount && parseFloat(cashAmount) >= finalAmount;
    }
    return true; // M-Pesa and Card are always ready (assuming external verification)
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Process Payment</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Order Total */}
          <div className="flex items-center justify-between">
            <span className="text-gray-600 dark:text-gray-400">Order Total</span>
            <span className="text-3xl font-bold text-orange-500">
              KSh {orderTotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </span>
          </div>

          {/* Discount Options */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-3">Discount:</label>
            <div className="flex gap-2">
              {discountOptions.map((discount) => (
                <button
                  key={discount}
                  onClick={() => setSelectedDiscount(discount)}
                  className={`flex-1 py-2 px-3 rounded-lg font-medium text-sm transition-all ${
                    selectedDiscount === discount
                      ? 'bg-red-500 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {discount}%
                </button>
              ))}
            </div>
          </div>

          {/* Show discounted amount if discount applied */}
          {selectedDiscount > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-700 dark:text-green-300">Discount ({selectedDiscount}%)</span>
                <span className="font-semibold text-green-700 dark:text-green-300">
                  - KSh {discountAmount.toFixed(2)}
                </span>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-green-200 dark:border-green-700">
                <span className="font-semibold text-green-800 dark:text-green-200">Final Amount</span>
                <span className="text-xl font-bold text-green-800 dark:text-green-200">
                  KSh {finalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </span>
              </div>
            </div>
          )}

          {/* Payment Method Selection */}
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-3">Payment Method:</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => setSelectedMethod('CASH')}
                className={`py-3 px-4 rounded-xl font-medium text-sm transition-all border-2 ${
                  selectedMethod === 'CASH'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Wallet className="w-5 h-5 mx-auto mb-1" />
                Cash
              </button>
              <button
                onClick={() => setSelectedMethod('MPESA')}
                className={`py-3 px-4 rounded-xl font-medium text-sm transition-all border-2 ${
                  selectedMethod === 'MPESA'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <Smartphone className="w-5 h-5 mx-auto mb-1" />
                M-Pesa
              </button>
              <button
                onClick={() => setSelectedMethod('CARD')}
                className={`py-3 px-4 rounded-xl font-medium text-sm transition-all border-2 ${
                  selectedMethod === 'CARD'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <CreditCard className="w-5 h-5 mx-auto mb-1" />
                Card
              </button>
            </div>
          </div>

          {/* Payment Method Specific UI */}
          <div className="min-h-[180px] flex items-center justify-center">
            {selectedMethod === 'CASH' && (
              <div className="w-full space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Amount Tendered (KSh)
                </label>
                <input
                  type="number"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {cashAmount && parseFloat(cashAmount) >= finalAmount && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-green-700 dark:text-green-300">Change</span>
                      <span className="font-semibold text-green-700 dark:text-green-300">
                        KSh {(parseFloat(cashAmount) - finalAmount).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {selectedMethod === 'MPESA' && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center mx-auto">
                  <Smartphone className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Confirm M-Pesa payment of
                </p>
                <p className="text-2xl font-bold text-orange-500">
                  KSh {finalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Verify transaction before completing
                </p>
              </div>
            )}

            {selectedMethod === 'CARD' && (
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto">
                  <CreditCard className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Confirm card payment of
                </p>
                <p className="text-2xl font-bold text-orange-500">
                  KSh {finalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Swipe or tap card on terminal
                </p>
              </div>
            )}
          </div>

          {/* Complete Payment Button */}
          <button
            onClick={handleCompletePayment}
            disabled={!isPaymentReady()}
            className={`w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
              isPaymentReady()
                ? 'bg-green-600 hover:bg-green-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
            }`}
          >
            <Check className="w-5 h-5" />
            Complete Payment
          </button>
        </div>
      </div>
    </div>
  );
}
